import React, { useMemo } from 'react';
import { useMutation } from 'react-query';
import {
	Flex,
	Grid,
} from 'antd';
import {
	AccountTypes,
	Currencies,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { formatCurrency } from '@shared/utils/currency';
import type { CreateFixtureExpenseRequest } from '@api/features/fixtures/createFixtureExpense';
import type { UpdateFixtureExpenseRequest } from '@api/features/fixtures/updateFixtureExpense';
import { TcTermsWithIncludes } from '@api/utils/getTcTerms';
import EditableTable from '@client/components/EditableTable';
import {
	createFixtureExpense,
	deleteFixtureExpense,
	updateFixtureExpense,
} from '@client/lib/api';

const TcExpensesTables = ({
	tcTerms,
	refreshFixture,
	currency,
	locked = false,
}: {
	tcTerms: TcTermsWithIncludes,
	refreshFixture: () => void;
	currency: Values<typeof Currencies>;
	locked?: boolean;
}) => {
	const screens = Grid.useBreakpoint();

	const createMutation = useMutation(
		({
			account,
			values,
		}: {
			account: CreateFixtureExpenseRequest['account'];
			values: Omit<CreateFixtureExpenseRequest, 'tcTermsId' | 'account'>;
		}) => {
			return createFixtureExpense(tcTerms.id, { account, ...values });
		},
		{ onSuccess: refreshFixture },
	);

	const updateMutation = useMutation(
		({ id, values }: {
			id: number;
			values: UpdateFixtureExpenseRequest['attributes'];
		}) => {
			return updateFixtureExpense(tcTerms.id, id, values);
		},
		{ onSuccess: refreshFixture },
	);

	const deleteMutation = useMutation(
		(id: number) => {
			return deleteFixtureExpense(tcTerms.id, id);
		},
		{ onSuccess: refreshFixture },
	);

	const handleCreateExpense = (
		account: CreateFixtureExpenseRequest['account'],
		values: Omit<CreateFixtureExpenseRequest, 'tcTermsId' | 'account'>,
	) => {
		return createMutation.mutateAsync({ account, values });
	};

	const handleSaveExpense = (
		id: number,
		values: UpdateFixtureExpenseRequest['attributes'],
	) => {
		return updateMutation.mutateAsync({ id, values });
	};

	const handleDeleteExpense = (id: number) => {
		return deleteMutation.mutateAsync(id);
	};

	const fixtureExpenses = useMemo(() => {
		return {
			chartererExpenses: tcTerms.FixtureExpenses.filter((e) => e.account === 'charterer'),
			ownerExpenses: tcTerms.FixtureExpenses.filter((e) => e.account === 'owner')
		};
	}, [tcTerms]);

	return (
		<Flex gap={16} vertical>
			<EditableTable
				key={'ce'+fixtureExpenses.chartererExpenses.length}
				iconButtons
				pagination={false}
				enableDelete={!locked}
				enableEdit={!locked}
				allowAddNew={!locked}
				addNewText="Charterer's expenses"
				emptyText={(<>No charterers expenses</>)}
				dataSource={fixtureExpenses.chartererExpenses}
				onAddNew={(values) => handleCreateExpense(AccountTypes.CHARTERER, values)}
				onSave={handleSaveExpense}
				onDelete={handleDeleteExpense}
				keyDataIndex="id"
				addButtonTestId="charts-add-button"
				useCards={screens.xs}
				columns={
					[
						{
							dataIndex: 'name',
							title: 'Name',
							editable: true,
							// @ts-ignore
							editingProps: {
								type: 'string',
								inputProps: {
									'data-testid': 'charts-name',
								}
							}
						},
						{
							dataIndex: 'amount',
							title: 'Amount',
							editable: true,
							editingProps: {
								type: 'currency',
								inputProps: {
									currency,
									'data-testid': 'charts-amount',
								},
							},
							render: (c: number) => c != null && formatCurrency(
								c,
								currency,
								{ forceDecimals: true },
							),
						},
					]
				}
			/>
			<EditableTable
				key={'oe'+fixtureExpenses.ownerExpenses.length}
				iconButtons
				pagination={false}
				enableDelete={!locked}
				enableEdit={!locked}
				allowAddNew={!locked}
				addNewText="Owner's expenses"
				emptyText={(<>No owners expenses</>)}
				dataSource={fixtureExpenses.ownerExpenses}
				onAddNew={(values) => handleCreateExpense(AccountTypes.OWNER, values)}
				onSave={handleSaveExpense}
				onDelete={handleDeleteExpense}
				keyDataIndex="id"
				addButtonTestId="owners-add-button"
				useCards={screens.xs}
				columns={
					[
						{
							dataIndex: 'name',
							title: 'Name',
							editable: true,
							// @ts-ignore
							editingProps: {
								type: 'string',
								inputProps: {
									'data-testid': 'owner-name',
								}
							}
						},
						{
							dataIndex: 'amount',
							title: 'Amount',
							editable: true,
							editingProps: {
								type: 'currency',
								inputProps: {
									currency,
									'data-testid': 'owner-amount',
								},
							},
							render: (c: number) => c != null && formatCurrency(
								c,
								currency,
								{ forceDecimals: true },
							),
						},
					]
				}
			/>
		</Flex>
	);
};

export default TcExpensesTables;
